import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Keyboard } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { applyForCorrection, createNewTradingAccount, getTradingAccounts, invalidateTradingAccountsCache } from "../utils/apiClient";
import { createDemoTradingAccount } from "../controller/accountsDemo/addDemoAccountController";
import AppConstants from "../utils/appConstants";
import { AccountType } from "../utils/enums";
import {
  getPermissionControllers,
  hasCustomerPermission,
  isAccountApprovalLocked,
  isAgentWithdrawalDisabled,
} from "../utils/permissionHelper";
import PermissionCustomerModelController from "../controller/model/PermissionCustomerModelController";
import { useToast } from "../contexts/ToastContext";

const DASHBOARD_ACCOUNTS_LIMIT = 50;
const VISIBLE_ACCOUNT_TABS = 3;

const toNumberSafe = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const formatMoney = (value) => {
  return `$${toNumberSafe(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatLeverage = (leverage) => {
  if (leverage === null || leverage === undefined || leverage === "") return "1/100";
  if (typeof leverage === "string" && (leverage.includes("/") || leverage.includes(":"))) {
    return leverage.replace(":", "/");
  }
  if (typeof leverage === "number") return `1/${leverage}`;
  return String(leverage);
};

const getAccountTypeCode = (account) => {
  const type =
    account?.TYPE ??
    account?.type ??
    account?.ACCOUNT_TYPE ??
    account?.account_type ??
    null;
  const typeNum = Number(type);
  return Number.isFinite(typeNum) ? typeNum : null;
};

const isExcludedAccountType = (account) => {
  const typeNum = getAccountTypeCode(account);
  return (
    typeNum === AccountType.WALLET ||
    typeNum === AccountType.DEMO ||
    typeNum === AccountType.ADMIN ||
    typeNum === AccountType.ADMIN_WALLET
  );
};

const isTradingAccount = (account) => {
  const typeNum = getAccountTypeCode(account);
  return typeNum === AccountType.REAL || typeNum === AccountType.BROKER;
};

const hasTradingAccount = (account) => {
  const tradingId = String(account?.tradingId ?? "").trim();
  return tradingId !== "" && tradingId !== "-";
};

const getLatestSortValue = (account) => {
  const info = account?.account_information || account?.accountInformation || {};
  const dateCandidates = [
    account?.CREATED_AT,
    account?.created_at,
    account?.DATE_CREATED,
    account?.date_created,
    account?.createdAt,
    info.CREATED_AT,
    info.created_at,
  ];

  for (const value of dateCandidates) {
    if (value == null || value === "") continue;
    if (typeof value === "number" && Number.isFinite(value)) {
      return value > 0 && value < 1e12 ? value * 1000 : value;
    }
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }

  const id = Number(account?.ACCOUNT_ID ?? account?.ID ?? account?.id);
  if (Number.isFinite(id)) return id;

  const tradingId = Number(info.TRADING_ID ?? account?.TRADING_ID ?? account?.trading_id);
  return Number.isFinite(tradingId) ? tradingId : 0;
};

const sortAccountsLatestFirst = (accounts) => {
  return [...accounts].sort((a, b) => getLatestSortValue(b) - getLatestSortValue(a));
};

const extractAccountsFromResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload.DATA)) return payload.DATA;
  if (payload.DATA && typeof payload.DATA === "object" && !Array.isArray(payload.DATA)) {
    return [payload.DATA];
  }
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.accounts)) return payload.accounts;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const mapDashboardAccount = (account, index) => {
  const accountInfo = account?.account_information || account?.accountInformation || {};
  const wallet =
    account?.wallet && Array.isArray(account.wallet) && account.wallet.length > 0
      ? account.wallet[0]
      : {};
  const typeNum = getAccountTypeCode(account);
  const nickRaw =
    account?.NICK ||
    account?.NICK_NAME ||
    account?.nick ||
    account?.nick_name ||
    account?.NICKNAME ||
    accountInfo.NAME ||
    accountInfo.name ||
    "";
  const nick = nickRaw === "-" ? "" : String(nickRaw).trim();

  return {
    id: account?.ID || account?.id || accountInfo.ACCOUNT_ID || `account-${index}`,
    accountId:
      accountInfo.ACCOUNT_ID ||
      accountInfo.account_id ||
      account?.ACCOUNT_ID ||
      account?.account_id ||
      account?.ID ||
      account?.id ||
      null,
    tradingId:
      accountInfo.TRADING_ID ||
      account?.TRADING_ID ||
      account?.trading_id ||
      account?.ID ||
      account?.id ||
      "-",
    nick,
    typeNum,
    isBroker: typeNum === AccountType.BROKER,
    typeLabel: typeNum === AccountType.BROKER ? "Broker" : "Real",
    walletId: wallet.ID || wallet.id || wallet.WALLET_ID || null,
    balance: toNumberSafe(
      wallet.CURRENT_BALENCE ??
        wallet.CURRENT_BALANCE ??
        wallet.BALANCE ??
        account?.BALANCE ??
        account?.balance,
      0
    ),
    equity: toNumberSafe(wallet.EQUITY ?? account?.EQUITY ?? account?.equity, 0),
    freeMargin: toNumberSafe(
      wallet.FREE_MARGIN ?? account?.FREE_MARGIN ?? account?.free_margin,
      0
    ),
    leverage: formatLeverage(accountInfo.LEVERAGE || account?.LEVERAGE || account?.leverage),
  };
};

const AccountSlideCard = ({
  account,
  t,
  canDeposit,
  canWithdraw,
  hideWithdraw,
  onDeposit,
  onWithdraw,
  onRequestCorrection,
  requestingCorrection,
}) => (
  <article className="dashboard-account-slide">
    <div className="dashboard-account-card-top">
      <div className="dashboard-account-id-wrap">
        <span className="dashboard-account-id-label">
          {t("trading_id", { ns: "mobile", defaultValue: "Trading ID" })}
        </span>
        <strong className="dashboard-account-id">{account.tradingId}</strong>
        {account.nick ? <span className="dashboard-account-nick">{account.nick}</span> : null}
      </div>
      <span className={`dashboard-account-type ${account.isBroker ? "is-broker" : "is-real"}`}>
        {account.typeLabel}
      </span>
    </div>

    <div className="dashboard-account-balance">
      <span>{t("balance", { ns: "mobile", defaultValue: "Balance" })}</span>
      <strong>{formatMoney(account.balance)}</strong>
    </div>

    <div className="dashboard-account-stats">
      <div>
        <span>{t("equity", { ns: "mobile", defaultValue: "Equity" })}</span>
        <strong>{formatMoney(account.equity)}</strong>
      </div>
      <div>
        <span>{t("free_margin", { ns: "mobile", defaultValue: "Free margin" })}</span>
        <strong>{formatMoney(account.freeMargin)}</strong>
      </div>
      <div>
        <span>{t("leverage", { ns: "mobile", defaultValue: "Leverage" })}</span>
        <strong>{account.leverage}</strong>
      </div>
    </div>

    <div className="dashboard-account-actions">
      <button
        type="button"
        className={`dashboard-account-action dashboard-account-action--deposit ${
          !canDeposit ? "is-disabled" : ""
        }`}
        onClick={() => onDeposit(account)}
        title={
          canDeposit
            ? t("deposit", { ns: "mobile", defaultValue: "Deposit" })
            : t("dont_have_permissions", { ns: "mobile" })
        }
      >
        <i className="fa-solid fa-arrow-up" />
        {t("deposit", { ns: "mobile", defaultValue: "Deposit" })}
      </button>
      {!hideWithdraw && (
        <button
          type="button"
          className={`dashboard-account-action dashboard-account-action--withdraw ${
            !canWithdraw ? "is-disabled" : ""
          }`}
          onClick={() => onWithdraw(account)}
          title={
            canWithdraw
              ? t("withdraw", { ns: "mobile", defaultValue: "Withdraw" })
              : t("dont_have_permissions", { ns: "mobile" })
          }
        >
          <i className="fa-solid fa-arrow-down" />
          {t("withdraw", { ns: "mobile", defaultValue: "Withdraw" })}
        </button>
      )}
      {account.equity < 0 && account.accountId != null && (
        <button
          type="button"
          className="dashboard-account-action dashboard-account-action--correction"
          onClick={() => onRequestCorrection(account)}
          disabled={requestingCorrection}
          title={t("request_correction", { defaultValue: "Request Balance Correction" })}
        >
          <i className={`fa-solid ${requestingCorrection ? "fa-spinner fa-spin" : "fa-bandage"}`} />
          {t("request_correction", { defaultValue: "Request Balance Correction" })}
        </button>
      )}
    </div>
  </article>
);

const DashboardAccounts = ({ user = null, compact = false, awaitReady = false }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("mobile");
  const { warning: showWarning, error: showError, success: showSuccess } = useToast();
  const [correctionRequestId, setCorrectionRequestId] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountKind, setAccountKind] = useState("real");
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [activeAccountIndex, setActiveAccountIndex] = useState(0);
  const accountsSwiperRef = useRef(null);

  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const { customerPerm, customerDevicePerm } = permissions;
  const approvalLocked = useMemo(() => isAccountApprovalLocked(user), [user]);
  const agentWithdrawalDisabled = useMemo(() => isAgentWithdrawalDisabled(user), [user]);

  const canDeposit =
    !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_USD_MT5
    );
  const canWithdraw =
    !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_USD
    );
  const canAddRealAccount =
    !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__ADD_REAL_ACCOUNT
    );
  const canAddDemoAccount =
    !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__ADD_DEMO_ACCOUNT
    );
  const canOpenAccount = accountKind === "demo" ? canAddDemoAccount : canAddRealAccount;
  const showLoading = loading || awaitReady;

  useEffect(() => {
    const handleRefresh = () => setRefreshTick((prev) => prev + 1);
    window.addEventListener("dashboard:refresh", handleRefresh);
    window.addEventListener("mfg:funds-refresh", handleRefresh);
    return () => {
      window.removeEventListener("dashboard:refresh", handleRefresh);
      window.removeEventListener("mfg:funds-refresh", handleRefresh);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAccounts = async () => {
      setLoading(true);
      try {
        const response = await getTradingAccounts(AccountType.REAL, DASHBOARD_ACCOUNTS_LIMIT, 1, null, {
          forceRefresh: refreshTick > 0,
        });
        const payload = response?.decryptedData || response?.data;
        let mapped = [];

        if (payload && Number(payload.CODE) === AppConstants.responseOk) {
          mapped = sortAccountsLatestFirst(
            extractAccountsFromResponse(payload).filter((account) => !isExcludedAccountType(account))
          )
            .map(mapDashboardAccount)
            .filter(hasTradingAccount)
            .slice(0, DASHBOARD_ACCOUNTS_LIMIT);
        }

        if (!cancelled) setAccounts(mapped);
      } catch {
        if (!cancelled) setAccounts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAccounts();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  useEffect(() => {
    if (loading || accounts.length > 0 || !Array.isArray(user?.account)) return;
    const fallback = sortAccountsLatestFirst(user.account.filter(isTradingAccount))
      .map(mapDashboardAccount)
      .filter(hasTradingAccount)
      .slice(0, DASHBOARD_ACCOUNTS_LIMIT);
    if (fallback.length > 0) setAccounts(fallback);
  }, [user, accounts.length, loading]);

  const goToInternalTransfer = (transferMode, walletId = null) => {
    navigate(
      {
        pathname: "/pages/transfer",
        search: `?mode=${encodeURIComponent(transferMode)}`,
      },
      {
        state: {
          transferMode,
          ...(walletId != null ? { walletId } : {}),
        },
      }
    );
  };

  const handleDeposit = (account) => {
    if (!canDeposit) {
      showWarning?.(
        "Permission required",
        t("dont_have_permissions", { ns: "mobile", defaultValue: "You don't have permission." })
      );
      return;
    }
    goToInternalTransfer("deposit", account.walletId);
  };

  const handleOpenAccount = async () => {
    if (creatingAccount || !canOpenAccount) {
      if (!canOpenAccount) {
        showWarning?.(
          "Permission required",
          t("dont_have_permissions", { defaultValue: "You don't have permission." })
        );
      }
      return;
    }

    const isDemo = accountKind === "demo";
    setCreatingAccount(true);
    try {
      const response = isDemo
        ? await createDemoTradingAccount()
        : await createNewTradingAccount(AccountType.REAL);
      const data = response?.decryptedData || response?.data || {};
      const code = Number(
        data?.CODE ?? data?.code ?? data?.BODY?.CODE ?? data?.body?.CODE ?? data?.body?.code
      );
      if (code !== AppConstants.responseOk) {
        showError?.(
          "Account creation failed",
          data?.MESSAGE ||
            data?.message ||
            (isDemo
              ? "Failed to create new Demo Account. Please try again."
              : "Failed to create new Real Account. Please try again.")
        );
        return;
      }
      invalidateTradingAccountsCache();
      navigate(isDemo ? "/pages/accounts/demo" : "/pages/accounts/list");
    } catch (err) {
      showError?.(
        "Account creation failed",
        err?.message || "Failed to create the account. Please try again."
      );
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleRequestCorrection = async (account) => {
    if (!account?.accountId || correctionRequestId) return;
    const requestKey = String(account.accountId);
    setCorrectionRequestId(requestKey);
    try {
      const response = await applyForCorrection(account.accountId);
      const data = response?.decryptedData || response?.data || {};
      const code = Number(data?.CODE ?? data?.code);
      if (code === AppConstants.responseOk) {
        showSuccess?.(
          t("request_correction", { defaultValue: "Request Balance Correction" }),
          data?.message ||
            data?.MESSAGE ||
            t("correction_request_submitted", {
              defaultValue: "Correction request submitted successfully",
            })
        );
        invalidateTradingAccountsCache();
        setRefreshTick((prev) => prev + 1);
      } else {
        showError?.(
          t("request_correction", { defaultValue: "Request Balance Correction" }),
          data?.message ||
            data?.MESSAGE ||
            t("failed_to_submit_correction_request", {
              defaultValue: "Failed to submit correction request",
            })
        );
      }
    } catch (_) {
      showError?.(
        t("request_correction", { defaultValue: "Request Balance Correction" }),
        t("try_again", { defaultValue: "Operation failed, please try again" })
      );
    } finally {
      setCorrectionRequestId(null);
    }
  };

  const handleWithdraw = (account) => {
    if (!canWithdraw) {
      showWarning?.(
        "Permission required",
        t("dont_have_permissions", { ns: "mobile", defaultValue: "You don't have permission." })
      );
      return;
    }
    goToInternalTransfer("withdraw", account.walletId);
  };

  useEffect(() => {
    setActiveAccountIndex((current) => {
      if (accounts.length === 0) return 0;
      return Math.min(current, accounts.length - 1);
    });
  }, [accounts]);

  const visibleAccountTabIndexes = useMemo(() => {
    const total = accounts.length;
    if (total <= 1) return [];
    if (total <= VISIBLE_ACCOUNT_TABS) {
      return Array.from({ length: total }, (_, index) => index);
    }

    const start = Math.max(
      0,
      Math.min(activeAccountIndex - 1, total - VISIBLE_ACCOUNT_TABS)
    );
    return Array.from({ length: VISIBLE_ACCOUNT_TABS }, (_, offset) => start + offset);
  }, [accounts.length, activeAccountIndex]);

  return (
    <section
      className={`dashboard-accounts-section ${compact ? "is-compact" : ""}`}
      data-page-tour="trading_accounts"
    >
      {!compact && (
        <div className="dashboard-accounts-header">
          <h3>
            <i className="fa-solid fa-layer-group" />
            {t("trading_accounts_title", { ns: "mobile", defaultValue: "Trading Accounts" })}
          </h3>
          <button
            type="button"
            className="dashboard-accounts-see-all"
            onClick={() => navigate("/pages/accounts/list")}
          >
            {t("see_all", { ns: "mobile", defaultValue: "See All" })}
            <i className="fa-solid fa-arrow-right" />
          </button>
        </div>
      )}

      <div
        className={`dashboard-account-card ${showLoading ? "is-loading" : ""} ${
          !showLoading && accounts.length === 0 ? "is-empty" : ""
        }`}
      >
        <div className="dashboard-account-title">
          <i className="fa-regular fa-credit-card" />
          <span>{t("trading_accounts_title", { ns: "mobile", defaultValue: "Trading Accounts" })}</span>
        </div>
        <div className="dashboard-account-divider" />

        {showLoading ? (
          <div className="dashboard-account-slide">
            <div className="dashboard-account-card-top">
              <span className="credit-card-data-shimmer credit-card-data-shimmer--tiny" />
              <span className="credit-card-data-shimmer credit-card-data-shimmer--tiny" />
            </div>
            <span className="credit-card-data-shimmer credit-card-data-shimmer--small" />
            <div className="dashboard-account-stats">
              <span className="credit-card-data-shimmer credit-card-data-shimmer--tiny" />
              <span className="credit-card-data-shimmer credit-card-data-shimmer--tiny" />
              <span className="credit-card-data-shimmer credit-card-data-shimmer--tiny" />
            </div>
          </div>
        ) : accounts.length === 0 ? (
          <div className="dashboard-accounts-empty">
            <button
              type="button"
              className={`dashboard-account-kind ${accountKind === "real" ? "is-selected" : ""}`}
              onClick={() => setAccountKind("real")}
              disabled={!canAddRealAccount || creatingAccount}
            >
              <span className="dashboard-account-kind-icon">
                <i className="fa-solid fa-chart-line" />
              </span>
              <span>
                <strong>{t("real_account", { defaultValue: "Real Account" })}</strong>
                <em>
                  {t("real_account_desc", {
                    defaultValue: "Trade in the live market with real funds.",
                  })}
                </em>
              </span>
            </button>
            <button
              type="button"
              className={`dashboard-account-kind ${accountKind === "demo" ? "is-selected" : ""}`}
              onClick={() => setAccountKind("demo")}
              disabled={!canAddDemoAccount || creatingAccount}
            >
              <span className="dashboard-account-kind-icon">
                <i className="fa-solid fa-flask" />
              </span>
              <span>
                <strong>{t("demo_account", { defaultValue: "Demo Account" })}</strong>
                <em>
                  {t("demo_account_desc", {
                    defaultValue: "Practice with virtual funds.",
                  })}
                </em>
              </span>
            </button>
            <button
              type="button"
              className="dashboard-accounts-add-btn"
              onClick={handleOpenAccount}
              disabled={creatingAccount || !canOpenAccount}
            >
              {creatingAccount
                ? t("creating", { defaultValue: "Creating..." })
                : t("open_account", { defaultValue: "Open Account" })}
              {!creatingAccount ? <i className="fa-solid fa-arrow-right" /> : null}
            </button>
          </div>
        ) : (
          <div className="dashboard-accounts-swiper-wrap">
            <Swiper
              modules={[Keyboard]}
              onSwiper={(instance) => {
                accountsSwiperRef.current = instance;
              }}
              onSlideChange={(instance) => {
                setActiveAccountIndex(instance.activeIndex);
              }}
              slidesPerView={1}
              spaceBetween={0}
              speed={420}
              dir={i18n.dir()}
              resistanceRatio={0.65}
              keyboard={{ enabled: accounts.length > 1 }}
              allowTouchMove={accounts.length > 1}
              className="dashboard-accounts-swiper"
            >
              {accounts.map((account) => (
                <SwiperSlide key={account.id}>
                  <AccountSlideCard
                    account={account}
                    t={t}
                    canDeposit={canDeposit}
                    canWithdraw={canWithdraw}
                    hideWithdraw={account.isBroker && agentWithdrawalDisabled}
                    onDeposit={handleDeposit}
                    onWithdraw={handleWithdraw}
                    onRequestCorrection={handleRequestCorrection}
                    requestingCorrection={correctionRequestId === String(account.accountId)}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            {visibleAccountTabIndexes.length > 0 && (
              <div
                className="dashboard-accounts-tabs"
                role="tablist"
                aria-label={t("trading_accounts_title", {
                  ns: "mobile",
                  defaultValue: "Trading Accounts",
                })}
              >
                {visibleAccountTabIndexes.map((index) => {
                  const tabAccount = accounts[index];
                  const isActive = index === activeAccountIndex;
                  return (
                    <button
                      key={`${tabAccount.id}-${index}`}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-label={`${tabAccount.tradingId} (${index + 1}/${accounts.length})`}
                      className={`dashboard-accounts-tab${isActive ? " is-active" : ""}`}
                      onClick={() => accountsSwiperRef.current?.slideTo(index)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default DashboardAccounts;
